import rest from '@data/restWrapper';
import {
    InvoiceControlerModel,
    InvoicePreview,
    ReservationModelWithModelsIncluded,
    GroupInfoWithReservations,
    InvoiceModelWithInvoiceItems,
    // InvoiceModel,
} from '@common/modelDefinition';

import { /*GuestInterface, */ RoomCleaningRequest, ReservationTypeBody, InvoiceTypeEnum } from './interfaces';
import { startGlobalLoading, stopGlobalLoading } from '@components/loaders/globalLoading';
import { getLoyaltyProgram } from './controler';

export async function getPendingInvoiceForReservation(
    reservationId: number | null,
    groupUuid: string | null,
    invoiceType: number
): Promise<InvoiceControlerModel> {
    startGlobalLoading();

    const loyaltyProgram = getLoyaltyProgram();
    const {
        invoice,
        invoiceItems,
        advanceInvoices,
        proformaInvoices,
        stornoInvoices,
        posTerminalInvoices,
        genuineInvoices,
        checkoutDateMismatchs,
        minibarInvoices,
        availableForAdvancePayment,
    } = await rest('/getPendingInvoiceForReservation', 'POST', {
        reservationId,
        groupUuid,
        invoiceType,
        loyaltyEnabled: loyaltyProgram?.loyaltyEnabled,
    });
    stopGlobalLoading();
    const invoiceControlerModel = {
        invoice,
        invoiceItems,
        advanceInvoices,
        proformaInvoices,
        stornoInvoices,
        posTerminalInvoices,
        genuineInvoices,
        checkoutDateMismatchs,
        minibarInvoices,
        availableForAdvancePayment,
    };
    return invoiceControlerModel;
}

export async function getBelongingReservation(
    reservationId: number | null
): Promise<ReservationModelWithModelsIncluded | null> {
    if (reservationId) {
        const reservation: ReservationModelWithModelsIncluded = await rest(`/Reservation/${reservationId}`);
        return reservation;
    } else return null;
}

export async function getBelongingGroupReservation(
    groupUuid: string | null,
    short: boolean = false
): Promise<GroupInfoWithReservations | null> {
    if (groupUuid) {
        const query = short ? '?short=true' : '';
        const groupReservation = await rest(`/getGroupInfo/` + groupUuid + query);
        return groupReservation;
    } else return null;
}

interface SetReservationInvoicePropertiesProps {
    invoiceId: number;
    properties: any;
    terminalType?: string;
    simpleView?: boolean;
}
export async function setReservationInvoiceProperties(
    props: SetReservationInvoicePropertiesProps
): Promise<InvoiceControlerModel> {
    const { invoiceId, properties, terminalType, simpleView = false } = props;
    const loyaltyProgram = getLoyaltyProgram();
    startGlobalLoading();
    const {
        invoice,
        invoiceItems,
        advanceInvoices,
        proformaInvoices,
        stornoInvoices,
        posTerminalInvoices,
        genuineInvoices,
        checkoutDateMismatchs,
        minibarInvoices,
        availableForAdvancePayment,
    } = await rest('/setReservationInvoiceProperties', 'POST', {
        invoiceId,
        properties: { ...properties },
        terminalType,
        loyaltyEnabled: loyaltyProgram?.loyaltyEnabled,
         simpleView,
    });
    stopGlobalLoading();
    const invoiceControlerModel = {
        invoice,
        invoiceItems,
        advanceInvoices,
        proformaInvoices,
        stornoInvoices,
        posTerminalInvoices,
        genuineInvoices,
        checkoutDateMismatchs,
        minibarInvoices,
        availableForAdvancePayment,
    };
    return invoiceControlerModel;
}

export async function getReservationInvoicePreview(reservationId: number): Promise<InvoicePreview> {
    const reservationInvoicePreview: InvoicePreview = await rest(`/getReservationInvoicePreview`, 'POST', {
        reservationId,
    });
    const { invoice, invoiceItems } = reservationInvoicePreview;
    return { invoice, invoiceItems };
}

export async function receptionSetToggleStates(remoteDeviceId: string, roomCleaningRequest: RoomCleaningRequest) {
    await rest('/src/receptionSetToggleStates/' + remoteDeviceId, 'POST', roomCleaningRequest);
}

export async function createOrJoinNotConnectedConsumptionInvoices(reservationTypeBody: ReservationTypeBody) {
    const loyaltyProgram = getLoyaltyProgram();
    startGlobalLoading();
    const invoiceData = await rest('/createOrJoinNotConnectedConsumptionInvoices', 'POST', {
        ...reservationTypeBody,
        loyaltyEnabled: loyaltyProgram?.loyaltyEnabled,
    });
    stopGlobalLoading();
    return invoiceData;
}



export async function saveInvoice(invoice: InvoiceModelWithInvoiceItems) {
    return await rest('/Invoice/' + invoice.id, 'PUT', invoice);
}

export async function getGeneralSettingsByPrefix() {
    const genericNotes = await rest('/getGeneralSettingsByPrefix/note_');
    return genericNotes;
}

export async function searchInvoice(searchString: string): Promise<InvoiceModelWithInvoiceItems[]> {
    const res = await rest(`/searchInvoice?search=${searchString}`);
    const newRes = res.filter(
        (r: InvoiceModelWithInvoiceItems) =>
            (r.invoiceType === InvoiceTypeEnum.proforma || r.invoiceType === InvoiceTypeEnum.proformaDispatched) &&
            r.reclamation === 0 &&
            !r.reservationId &&
            !r.groupUuid &&
            r.invoiceNumberFull
    );
    return newRes;
}

interface InvoiceTemplate {
    pendingInvoice: number;
    currencyExchangeRate: number;
    invoiceType: number;
    taxIncludedInPrice: boolean;
    terminalType: string;
    posTerminalId: number;
    defaultCurrency: string;
    currency: string;
    locale: string;
    generalDiscountPercentage: number | null;
    typeOfPayment: string | null;
    operatorUuid: string;
    operatorNam: string;
}

export async function issuePosInvoice(
    posTerminalConsumption: any[],
    posTerminalId: number,
    reservationId: number | string | null,
    invoice: InvoiceTemplate,
    disconnectInvoiceIds?: number[] | null  // <-- NOVI PARAMETAR
) {
    const newInvoice = { ...invoice };
    newInvoice.typeOfPayment = null;
    newInvoice.invoiceType = InvoiceTypeEnum.proforma;
    
    await rest('/issuePosTerminalInvoice', 'POST', {
        posTerminalId: posTerminalId,
        posTerminalConsumption: posTerminalConsumption,
        reservationId,
        invoice: newInvoice,
        disconnectInvoiceIds,  // <-- ŠALJEMO NA BACKEND
    });
}

export async function loadPosTerminalPendigInvoice(
    posTerminalId: number,
    customerId: number | null,
    companyId: number | null,
    reservationId: number | null,
    groupUuid: string | null
) {
    const { invoice } = await rest('/getPendingInvoicePosTerminal', 'POST', {
        posTerminalId: posTerminalId,
    });

    if (invoice) {
        invoice.customerId = customerId;
        invoice.companyId = companyId;
        invoice.reservationId = reservationId;
        invoice.groupUuid = groupUuid;

        return invoice;
    }
}

export async function getGroupInfo(groupUuid: string) {
    return await rest('/getGroupInfo/' + groupUuid);
}
export async function getPosItems() {
    const { data: _posItems } = await rest('/getPosItemsWithSkuPluRates', 'GET');
    return _posItems;
}

export async function getVirtualStornoInvoice(invoiceId: number) {
    startGlobalLoading();
    const invoice = await rest('/getVirtualStornoInvoice/' + invoiceId);
    stopGlobalLoading();

    if (invoice) {
        return invoice;
    }
}

export async function updateInvoiceAndRecalculateStatus(
    invoice: InvoiceModelWithInvoiceItems,
    reservationId: number | null | undefined,
    groupUuid: string | null | undefined,
    add: boolean
): Promise<{ success: boolean }> {
    if (!invoice.id) {
        throw new Error('Invoice must have an ID');
    }

    if (
        invoice.invoiceType === InvoiceTypeEnum.proforma ||
        invoice.invoiceType === InvoiceTypeEnum.proformaDispatched
    ) {
        return await rest(`/updateInvoiceAndRecalculateStatus/${invoice.id}`, 'PUT', {
            reservationId: reservationId,
            groupUuid: groupUuid,
            add,
        });
    } else {
          throw new Error('Only a proforma invoice or a proforma dispatched invoice can be issued.');
    }
}
